import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { codeLabel } from '../../utils/format';

const fmtDate = v => {
  if (!v) return '–';
  try { return format(parseISO(v), 'd MMM yyyy'); } catch { return '–'; }
};

const STATUS_MAP = {
  booked:    'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  pending:   'bg-amber-100 text-amber-700',
};

const statusBadge = v => (
  <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize', STATUS_MAP[v] || 'bg-slate-100 text-slate-600')}>
    {v || '–'}
  </span>
);

const COLS = [
  { key: 'client_name',  label: 'Client' },
  { key: 'shift_date',   label: 'Shift Date',  render: v => fmtDate(v) },
  { key: 'start_time',   label: 'Start' },
  { key: 'end_time',     label: 'End' },
  { key: 'shift_type',   label: 'Shift Type',  render: v => codeLabel(v) || '–' },
  { key: 'status',       label: 'Status',      render: statusBadge },
  { key: 'staff_name',   label: 'Staff' },
  { key: 'note_type',    label: 'Note Type',   render: (v, row) => row.note_content ? (codeLabel(v) || '–') : '–' },
  {
    key: 'note_content',
    label: 'Content',
    render: v => v
      ? (
        <span title={v} className="text-xs">
          {(v || '').slice(0, 120)}{(v || '').length > 120 ? '…' : ''}
        </span>
      )
      : <span className="text-slate-400">–</span>,
  },
  { key: 'mood',         label: 'Mood',        render: (v, row) => row.note_content ? (v ? `${v}/5` : '–') : '–' },
];

export default function ClientServiceProgressNotes() {
  const [data, setData]         = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]   = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [clientId, setClientId] = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (clientId) p.set('client_id', clientId);
    api.get(`/reports/client-service-progress-notes?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, clientId]);

  useEffect(() => { load(); }, [load]);

  const selClient = clients.find(c => String(c.id) === String(clientId));

  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    ...(selClient ? [{ label: 'Client', value: `${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
  ];

  const notesCount = data.filter(r => r.note_content).length;

  return (
    <ReportLayout
      title="Client Service & Progress Notes"
      data={data}
      filename="client_service_progress_notes"
      columns={COLS}
      activeChips={chips}
      showFiltersPanel={showFilters}
      onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }
    >
      <div className="flex items-center gap-6 text-sm text-slate-600 font-medium">
        <span>Total shifts: <strong className="text-slate-900">{data.length}</strong></span>
        <span>Notes recorded: <strong className="text-slate-900">{notesCount}</strong></span>
      </div>
      <ReportTable
        columns={COLS}
        data={data}
        loading={loading}
        pagination={pagination}
        emptyMsg="No service records found for this period."
      />
    </ReportLayout>
  );
}
